import { get, post, put, del } from '../utils/request';

// Reminder status enum
export enum ReminderStatus {
  PENDING = 'pending',
  SENT = 'sent',
  CANCELLED = 'cancelled',
}

// Reminder repeat type enum
export enum ReminderRepeatType {
  NONE = 'none',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  CUSTOM = 'custom',
}

// Reminder method enum
export enum ReminderMethod {
  SYSTEM = 'system',
  WECHAT = 'wechat',
  SMS = 'sms',
  EMAIL = 'email',
}

// Reminder interface
export interface Reminder {
  id: number;
  target_type: string;
  target_id: number;
  title: string;
  content?: string;
  remind_time: string;
  remind_methods: ReminderMethod[];
  recipients: number[];
  repeat_type: ReminderRepeatType;
  repeat_config?: Record<string, any>;
  status: ReminderStatus;
  created_by: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

// Create reminder request interface
export interface CreateReminderRequest {
  target_type: string;
  target_id: number;
  title: string;
  content?: string;
  remind_time: string;
  remind_methods: ReminderMethod[];
  recipients: number[];
  repeat_type?: ReminderRepeatType;
  repeat_config?: Record<string, any>;
}

// Update reminder request interface
export interface UpdateReminderRequest {
  title?: string;
  content?: string;
  remind_time?: string;
  remind_methods?: ReminderMethod[];
  recipients?: number[];
  repeat_type?: ReminderRepeatType;
  repeat_config?: Record<string, any>;
  status?: ReminderStatus;
}

/**
 * Create reminder
 */
export const createReminder = async (data: CreateReminderRequest): Promise<Reminder> => {
  return post<Reminder>('/reminders', data);
};

/**
 * Get reminder list
 */
export const getReminderList = async (params: {
  target_type?: string;
  target_id?: number;
  status?: ReminderStatus;
  page?: number;
  page_size?: number;
}): Promise<Reminder[]> => {
  return get<Reminder[]>('/reminders', { params });
};

/**
 * Get reminder by ID
 */
export const getReminderById = async (id: number): Promise<Reminder> => {
  return get<Reminder>(`/reminders/${id}`);
};

/**
 * Update reminder
 */
export const updateReminder = async (
  id: number,
  data: UpdateReminderRequest
): Promise<Reminder> => {
  return put<Reminder>(`/reminders/${id}`, data);
};

/**
 * Delete reminder
 */
export const deleteReminder = async (id: number): Promise<{ msg: string }> => {
  return del<{ msg: string }>(`/reminders/${id}`);
};

/**
 * Snooze reminder
 */
export const snoozeReminder = async (
  id: number,
  minutes: number
): Promise<Reminder> => {
  return post<Reminder>(`/reminders/${id}/snooze`, { minutes });
};

/**
 * Get reminders for case
 */
export const getCaseReminders = async (caseId: number): Promise<Reminder[]> => {
  return getReminderList({ target_type: 'case', target_id: caseId });
};

/**
 * Get reminders for lead
 */
export const getLeadReminders = async (leadId: number): Promise<Reminder[]> => {
  return getReminderList({ target_type: 'lead', target_id: leadId });
};

/**
 * Get pending reminders
 */
export const getPendingReminders = async (): Promise<Reminder[]> => {
  return getReminderList({ status: ReminderStatus.PENDING });
};
